import { Theme } from '@monorepo/relayably/theme'
import { LinearGradient } from 'expo-linear-gradient'
import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Easing,
  Image,
  Platform,
  Pressable,
  Animated as ReactNativeAnimated,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native'

const logo = require('../../../../../assets/relayably/logo-256.png')

type StepAtmosphere = {
  glowColors?: [string, string, string]
  railAccent?: string
  sideBackground?: string
}

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '')
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const OnboardingLayout = ({
  accentLabel,
  atmosphere,
  children,
  currentStep,
  highlights,
  onLogout,
  subtitle,
  theme,
  title,
  totalSteps,
}: {
  accentLabel?: string
  atmosphere?: StepAtmosphere
  children: ReactNode
  currentStep: number
  highlights?: string[]
  onLogout?: () => void
  subtitle?: string
  theme: Theme
  title: string
  totalSteps: number
}) => {
  const styles = createStyles(theme)
  const { width } = useWindowDimensions()
  const isLargeScreen = width >= 960
  const fallbackAtmosphere: Required<StepAtmosphere> = {
    glowColors: [hexToRgba(theme.colors.primary, 0.22), hexToRgba(theme.colors.secondary, 0.1), 'transparent'],
    railAccent: theme.colors.primary,
    sideBackground: theme.dark ? '#000000' : theme.colors.surface,
  }
  const resolvedAtmosphere = {
    glowColors: atmosphere?.glowColors ?? fallbackAtmosphere.glowColors,
    railAccent: atmosphere?.railAccent ?? fallbackAtmosphere.railAccent,
    sideBackground: atmosphere?.sideBackground ?? fallbackAtmosphere.sideBackground,
  } satisfies Required<StepAtmosphere>
  const targetContent = {
    accentLabel,
    highlights: highlights ?? [],
    subtitle: subtitle ?? '',
    title,
  }
  const [displayedAtmosphere, setDisplayedAtmosphere] = useState(resolvedAtmosphere)
  const [overlayAtmosphere, setOverlayAtmosphere] = useState<Required<StepAtmosphere> | null>(null)
  const [displayedContent, setDisplayedContent] = useState(targetContent)
  const overlayOpacity = useRef(new ReactNativeAnimated.Value(0)).current
  const contentOpacity = useRef(new ReactNativeAnimated.Value(1)).current
  const progressValue = useRef(new ReactNativeAnimated.Value(currentStep / totalSteps)).current
  const gradientShift = useRef(new ReactNativeAnimated.Value(0)).current
  const overlayGradientShift = useRef(new ReactNativeAnimated.Value(12)).current
  const transitionKeyRef = useRef(
    JSON.stringify({
      accentLabel,
      atmosphere: resolvedAtmosphere,
      currentStep,
      highlights: highlights ?? [],
      subtitle: subtitle ?? '',
      title,
      totalSteps,
    })
  )

  useEffect(() => {
    ReactNativeAnimated.timing(progressValue, {
      toValue: currentStep / totalSteps,
      duration: 360,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    }).start()
  }, [currentStep, progressValue, totalSteps])

  useEffect(() => {
    const nextTransitionKey = JSON.stringify({
      accentLabel,
      atmosphere: resolvedAtmosphere,
      currentStep,
      highlights: highlights ?? [],
      subtitle: subtitle ?? '',
      title,
      totalSteps,
    })

    if (transitionKeyRef.current === nextTransitionKey) {
      return
    }

    transitionKeyRef.current = nextTransitionKey
    // overlayOpacity.stopAnimation()
    contentOpacity.stopAnimation()
    // gradientShift.stopAnimation()
    // overlayGradientShift.stopAnimation()
    // overlayOpacity.setValue(0)
    // gradientShift.setValue(0)
    // overlayGradientShift.setValue(12)

    setOverlayAtmosphere(resolvedAtmosphere)

    ReactNativeAnimated.parallel([
      ReactNativeAnimated.timing(contentOpacity, {
        toValue: 0,
        duration: 160,
        easing: Easing.out(Easing.quad),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (!finished) {
        return
      }

      setDisplayedAtmosphere(resolvedAtmosphere)
      setDisplayedContent(targetContent)
      setOverlayAtmosphere(null)
      contentOpacity.setValue(0)

      ReactNativeAnimated.timing(contentOpacity, {
        toValue: 1,
        duration: 240,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start()
    })
  }, [accentLabel, contentOpacity, currentStep, highlights, overlayOpacity, resolvedAtmosphere, targetContent, title, subtitle, totalSteps])

  const sideBackground = displayedAtmosphere.sideBackground
  const railAccent = resolvedAtmosphere.railAccent
  const glowColors = displayedAtmosphere.glowColors
  const progressWidth = progressValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  })
  const baseGlowTransform = [{ translateX: gradientShift }, { translateY: ReactNativeAnimated.multiply(gradientShift, -0.35) }]
  const overlayGlowTransform = [
    { translateX: overlayGradientShift },
    { translateY: ReactNativeAnimated.multiply(overlayGradientShift, -0.35) },
  ]

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.container}>
        {isLargeScreen ? (
          <View style={[styles.side, styles.sideDesktop]}>
            <View style={[styles.sideGradient, { backgroundColor: sideBackground }]}>
              <View style={styles.brandRow}>
                <View style={[styles.logoGlow, { backgroundColor: hexToRgba(railAccent, 0.22) }]} />
                <Image source={logo} style={styles.logo} />
                <Text style={styles.brandText}>Relayably</Text>
              </View>

              <View style={styles.sideCopy}>
                <View style={styles.progressTrack}>
                  <ReactNativeAnimated.View style={[styles.progressFill, { width: progressWidth as any, backgroundColor: railAccent }]} />
                </View>
                <ReactNativeAnimated.View style={{ opacity: contentOpacity }}>
                  <Text style={[styles.stepLabel, { color: railAccent }]}>
                    Step {currentStep} of {totalSteps}
                  </Text>
                  {displayedContent.accentLabel ? <Text style={styles.accentLabel}>{displayedContent.accentLabel}</Text> : null}
                  <Text style={styles.title}>{displayedContent.title}</Text>
                  <Text style={styles.subtitle}>{displayedContent.subtitle}</Text>
                  {displayedContent.highlights.length ? (
                    <View style={styles.highlightList}>
                      {displayedContent.highlights.map(item => (
                        <View key={item} style={styles.highlightRow}>
                          <View style={styles.highlightDot} />
                          <Text style={styles.highlightText}>{item}</Text>
                        </View>
                      ))}
                    </View>
                  ) : null}
                </ReactNativeAnimated.View>
              </View>

              <View style={styles.sideFooter}>
                {onLogout ? (
                  <Pressable onPress={onLogout} style={styles.logoutButton} hitSlop={10}>
                    <Text style={styles.logoutButtonLabel}>Log out</Text>
                  </Pressable>
                ) : null}
              </View>

              <LinearGradient colors={glowColors} end={{ x: 1, y: 1 }} start={{ x: 0, y: 0 }} style={styles.brandingGlowLarge} />
              <LinearGradient
                colors={['transparent', hexToRgba(railAccent, 0.08), hexToRgba(railAccent, 0)]}
                end={{ x: 1, y: 1 }}
                start={{ x: 0, y: 0.1 }}
                style={styles.brandingGlowEdge}
              />
              <ReactNativeAnimated.View pointerEvents='none' style={[styles.gradientMotionLayer, { transform: baseGlowTransform }]}>
                <LinearGradient colors={glowColors} end={{ x: 1, y: 1 }} start={{ x: 0, y: 0 }} style={styles.brandingGlowLarge} />
                <LinearGradient
                  colors={['transparent', hexToRgba(railAccent, 0.08), hexToRgba(railAccent, 0)]}
                  end={{ x: 1, y: 1 }}
                  start={{ x: 0, y: 0.1 }}
                  style={styles.brandingGlowEdge}
                />
              </ReactNativeAnimated.View>
              {overlayAtmosphere ? (
                <ReactNativeAnimated.View pointerEvents='none' style={[styles.atmosphereOverlay, { opacity: overlayOpacity }]}>
                  <View style={[styles.atmosphereOverlayBackground, { backgroundColor: overlayAtmosphere.sideBackground }]} />
                  <ReactNativeAnimated.View pointerEvents='none' style={[styles.gradientMotionLayer, { transform: overlayGlowTransform }]}>
                    <LinearGradient
                      colors={overlayAtmosphere.glowColors}
                      end={{ x: 1, y: 1 }}
                      start={{ x: 0, y: 0 }}
                      style={styles.brandingGlowLarge}
                    />
                    <LinearGradient
                      colors={['transparent', hexToRgba(overlayAtmosphere.railAccent, 0.08), hexToRgba(overlayAtmosphere.railAccent, 0)]}
                      end={{ x: 1, y: 1 }}
                      start={{ x: 0, y: 0.1 }}
                      style={styles.brandingGlowEdge}
                    />
                  </ReactNativeAnimated.View>
                </ReactNativeAnimated.View>
              ) : null}
            </View>
          </View>
        ) : null}

        <View style={[styles.main, isLargeScreen ? styles.mainDesktop : undefined]}>
          <ScrollView contentContainerStyle={styles.mainInner} showsVerticalScrollIndicator={false}>
            {!isLargeScreen ? (
              <View style={styles.mobileHeader}>
                <View style={styles.mobileHeaderTopRow}>
                  <View style={styles.brandRow}>
                    <View style={[styles.logoGlow, { backgroundColor: hexToRgba(railAccent, 0.22) }]} />
                    <Image source={logo} style={styles.logo} />
                    <Text style={styles.brandTextDark}>Relayably</Text>
                  </View>
                  {onLogout ? (
                    <Pressable onPress={onLogout} style={styles.logoutButtonMobile} hitSlop={10}>
                      <Text style={styles.logoutButtonMobileLabel}>Log out</Text>
                    </Pressable>
                  ) : null}
                </View>
                <View style={styles.progressTrackLight}>
                  <ReactNativeAnimated.View style={[styles.progressFill, { width: progressWidth as any, backgroundColor: railAccent }]} />
                </View>
              </View>
            ) : null}

            {children}
          </ScrollView>
        </View>
      </View>
    </SafeAreaView>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    container: {
      flex: 1,
      flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    },
    side: {
      minHeight: Platform.OS === 'web' ? undefined : 260,
    },
    sideDesktop: {
      borderRightColor: hexToRgba(theme.colors.primary, 0.12),
      borderRightWidth: 1,
      flex: 1,
      maxWidth: 420,
    },
    sideGradient: {
      backgroundColor: theme.dark ? '#000000' : theme.colors.surface,
      flex: 1,
      justifyContent: 'space-between',
      overflow: 'hidden',
      padding: 32,
      position: 'relative',
    },
    brandRow: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 14,
      position: 'relative',
    },
    logoutButton: {
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(255,255,255,0.08)',
      borderColor: 'rgba(255,255,255,0.16)',
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 14,
      paddingVertical: 9,
    },
    logoutButtonLabel: {
      color: '#f9f7ff',
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.3,
    },
    logo: {
      backgroundColor: theme.colors.card,
      borderColor: theme.colors.border,
      borderRadius: 18,
      borderWidth: 1,
      height: 44,
      width: 44,
      zIndex: 1,
    },
    logoGlow: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.2),
      borderRadius: 999,
      height: 40,
      left: 2,
      position: 'absolute',
      width: 40,
    },
    brandText: {
      color: '#f9f7ff',
      fontFamily: 'Georgia',
      fontSize: 24,
      fontWeight: '700',
    },
    brandTextDark: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 24,
      fontWeight: '700',
    },
    sideCopy: {
      flex: 1,
      justifyContent: 'center',
    },
    sideFooter: {
      alignItems: 'flex-start',
      gap: 14,
      zIndex: 1,
    },
    progressTrack: {
      backgroundColor: 'rgba(255,255,255,0.14)',
      borderRadius: 999,
      height: 4,
      marginBottom: 28,
      overflow: 'hidden',
      width: '100%',
    },
    progressTrackLight: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderRadius: 999,
      height: 4,
      marginTop: 18,
      overflow: 'hidden',
      width: '100%',
    },
    progressFill: {
      backgroundColor: theme.colors.primary,
      borderRadius: 999,
      height: '100%',
    },
    stepLabel: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 1.6,
      marginBottom: 16,
      textTransform: 'uppercase',
    },
    accentLabel: {
      color: 'rgba(255,255,255,0.58)',
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 1.4,
      marginBottom: 14,
      textTransform: 'uppercase',
    },
    title: {
      color: '#f9f7ff',
      fontFamily: 'Georgia',
      fontSize: 36,
      lineHeight: 42,
      marginBottom: 12,
    },
    subtitle: {
      color: 'rgba(255,255,255,0.68)',
      fontSize: 14,
      lineHeight: 22,
    },
    highlightList: {
      gap: 10,
      marginTop: 24,
      maxWidth: 300,
    },
    highlightRow: {
      alignItems: 'flex-start',
      flexDirection: 'row',
      gap: 10,
    },
    highlightDot: {
      backgroundColor: theme.colors.primary,
      borderRadius: 999,
      height: 8,
      marginTop: 7,
      width: 8,
    },
    highlightText: {
      color: 'rgba(255,255,255,0.78)',
      flex: 1,
      fontSize: 13,
      lineHeight: 20,
    },
    footnote: {
      color: 'rgba(255,255,255,0.46)',
      fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
      fontSize: 11,
      letterSpacing: 1.2,
      zIndex: 1,
    },
    brandingGlowLarge: {
      ...StyleSheet.absoluteFillObject,
      opacity: 1,
      pointerEvents: 'none',
    },
    brandingGlowEdge: {
      ...StyleSheet.absoluteFillObject,
      opacity: 1,
      pointerEvents: 'none',
    },
    gradientMotionLayer: {
      ...StyleSheet.absoluteFillObject,
      pointerEvents: 'none',
    },
    atmosphereOverlay: {
      ...StyleSheet.absoluteFillObject,
    },
    atmosphereOverlayBackground: {
      ...StyleSheet.absoluteFillObject,
    },
    main: {
      flex: 1,
    },
    mainDesktop: {
      flex: 1.45,
      justifyContent: 'center',
    },
    mainInner: {
      flexGrow: 1,
      justifyContent: 'center',
      maxWidth: 760,
      padding: 24,
      width: '100%',
      alignSelf: 'center',
    },
    mobileHeader: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 24,
    },
    mobileHeaderTopRow: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 12,
      width: '100%',
    },
    logoutButtonMobile: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      borderColor: hexToRgba(theme.colors.primary, 0.18),
      borderRadius: 999,
      borderWidth: 1,
      paddingHorizontal: 14,
      paddingVertical: 9,
    },
    logoutButtonMobileLabel: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.3,
    },
  })
